/**
 * ImageGallery Component
 *
 * Professional image gallery with carousel and lightbox functionality.
 * Supports multiple images per feature with navigation controls.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  IconButton,
  Dialog,
  DialogContent,
  Typography,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  FiberManualRecord as DotIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { landingColors, landingShadows } from '../../theme/landingTheme';

interface ImageGalleryProps {
  images: string[];
  title?: string;
  autoPlay?: boolean;
  autoPlayInterval?: number;
  aspectRatio?: number;
  showControls?: boolean;
  showDots?: boolean;
  showZoomHint?: boolean;
  borderRadius?: number;
  accentColor?: string;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({
  images,
  title = '',
  autoPlay = false,
  autoPlayInterval = 5000,
  aspectRatio = 16 / 10,
  showControls = true,
  showDots = true,
  showZoomHint = true,
  borderRadius = 16,
  accentColor = landingColors.primary.main,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [imageErrors, setImageErrors] = useState<Record<number, boolean>>({});

  const hasMultipleImages = images.length > 1;

  // Auto-play functionality
  useEffect(() => {
    if (!autoPlay || !hasMultipleImages || isHovered || lightboxOpen) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [autoPlay, autoPlayInterval, hasMultipleImages, isHovered, lightboxOpen, images.length]);

  const goToPrevious = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const goToNext = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const goToIndex = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'Escape') setLightboxOpen(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, goToPrevious, goToNext]);

  const handleImageError = (index: number) => {
    setImageErrors((prev) => ({ ...prev, [index]: true }));
  };

  // If no valid images, show styled placeholder
  if (images.length === 0 || images.every((_, i) => imageErrors[i])) {
    return (
      <Box
        sx={{
          width: '100%',
          paddingTop: `${(1 / aspectRatio) * 100}%`,
          position: 'relative',
          bgcolor: alpha(accentColor, 0.06),
          borderRadius: `${borderRadius}px`,
          border: `1px dashed ${alpha(accentColor, 0.25)}`,
          background: `linear-gradient(135deg, ${alpha(accentColor, 0.04)} 0%, ${alpha(accentColor, 0.1)} 100%)`,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: alpha(accentColor, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ImageIcon sx={{ fontSize: 32, color: alpha(accentColor, 0.5) }} />
          </Box>
          <Typography
            sx={{
              color: alpha(accentColor, 0.6),
              fontWeight: 500,
              fontSize: '0.95rem',
              textAlign: 'center',
              px: 2,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              color: landingColors.neutral.gray400,
              fontSize: '0.8rem',
            }}
          >
            Imagem em breve
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <>
      {/* Main Gallery */}
      <Box
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          position: 'relative',
          width: '100%',
          cursor: 'pointer',
          borderRadius: `${borderRadius}px`,
          overflow: 'hidden',
          boxShadow: landingShadows.card,
          bgcolor: '#FFFFFF',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: landingShadows.cardHover,
          },
        }}
      >
        {/* Image Container with Aspect Ratio */}
        <Box
          sx={{
            width: '100%',
            paddingTop: `${(1 / aspectRatio) * 100}%`,
            position: 'relative',
          }}
        >
          {/* Images */}
          {images.map((src, index) => (
            <Box
              key={index}
              onClick={() => setLightboxOpen(true)}
              sx={{
                position: 'absolute',
                inset: 0,
                opacity: index === currentIndex ? 1 : 0,
                transition: 'opacity 0.4s ease',
                pointerEvents: index === currentIndex ? 'auto' : 'none',
              }}
            >
              {!imageErrors[index] ? (
                <Box
                  component="img"
                  src={src}
                  alt={`${title} - ${index + 1}`}
                  loading="lazy"
                  decoding="async"
                  onError={() => handleImageError(index)}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    bgcolor: alpha(accentColor, 0.05),
                    background: `linear-gradient(135deg, ${alpha(accentColor, 0.04)} 0%, ${alpha(accentColor, 0.1)} 100%)`,
                  }}
                >
                  <ImageIcon sx={{ fontSize: 32, color: alpha(accentColor, 0.4) }} />
                  <Typography sx={{ color: landingColors.neutral.gray400, fontSize: '0.85rem' }}>
                    Imagem não disponível
                  </Typography>
                </Box>
              )}
            </Box>
          ))}

          {/* Zoom Hint Overlay */}
          {showZoomHint && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(0,0,0,0.3)',
                opacity: isHovered ? 1 : 0,
                transition: 'opacity 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: '#FFFFFF',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                }}
              >
                <ZoomInIcon fontSize="small" />
                <Typography variant="body2" fontWeight={500}>
                  Clique para ampliar
                </Typography>
              </Box>
            </Box>
          )}

          {/* Navigation Arrows */}
          {showControls && hasMultipleImages && (
            <>
              <IconButton
                onClick={goToPrevious}
                sx={{
                  position: 'absolute',
                  left: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255,255,255,0.9)',
                  opacity: isHovered ? 1 : 0,
                  transition: 'opacity 0.3s ease',
                  '&:hover': { bgcolor: '#FFFFFF' },
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                onClick={goToNext}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255,255,255,0.9)',
                  opacity: isHovered ? 1 : 0,
                  transition: 'opacity 0.3s ease',
                  '&:hover': { bgcolor: '#FFFFFF' },
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </>
          )}
        </Box>

        {/* Dots Indicator */}
        {showDots && hasMultipleImages && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 0.5,
              py: 1.5,
              bgcolor: alpha(landingColors.neutral.gray100, 0.8),
            }}
          >
            {images.map((_, index) => (
              <IconButton
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  goToIndex(index);
                }}
                sx={{
                  p: 0.25,
                  color: index === currentIndex ? accentColor : landingColors.neutral.gray300,
                  transition: 'color 0.2s ease',
                }}
              >
                <DotIcon sx={{ fontSize: index === currentIndex ? 12 : 10 }} />
              </IconButton>
            ))}
          </Box>
        )}
      </Box>

      {/* Lightbox Dialog */}
      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            maxWidth: '95vw',
            maxHeight: '95vh',
            m: 1,
          },
        }}
        slotProps={{
          backdrop: {
            sx: { bgcolor: 'rgba(0,0,0,0.9)' },
          },
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={() => setLightboxOpen(false)}
            sx={{
              position: 'fixed',
              top: 16,
              right: 16,
              color: '#FFFFFF',
              bgcolor: 'rgba(0,0,0,0.5)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Navigation Arrows */}
          {hasMultipleImages && (
            <>
              <IconButton
                onClick={goToPrevious}
                sx={{
                  position: 'fixed',
                  left: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#FFFFFF',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                }}
              >
                <ChevronLeftIcon fontSize="large" />
              </IconButton>
              <IconButton
                onClick={goToNext}
                sx={{
                  position: 'fixed',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#FFFFFF',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                }}
              >
                <ChevronRightIcon fontSize="large" />
              </IconButton>
            </>
          )}

          {/* Image */}
          <Box
            component="img"
            src={images[currentIndex]}
            alt={`${title} - ${currentIndex + 1}`}
            sx={{
              maxWidth: '100%',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: 2,
            }}
          />

          {/* Image Counter */}
          {hasMultipleImages && (
            <Box
              sx={{
                position: 'fixed',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                color: '#FFFFFF',
                bgcolor: 'rgba(0,0,0,0.6)',
                px: 2,
                py: 0.5,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant="body2">
                {currentIndex + 1} / {images.length}
              </Typography>
            </Box>
          )}

          {/* Dots for Mobile */}
          {hasMultipleImages && isMobile && (
            <Box
              sx={{
                position: 'fixed',
                bottom: 50,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: 1,
              }}
            >
              {images.map((_, index) => (
                <Box
                  key={index}
                  onClick={() => goToIndex(index)}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: index === currentIndex ? '#FFFFFF' : 'rgba(255,255,255,0.4)',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                  }}
                />
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImageGallery;
